import { gql } from '@apollo/client'

import { AUDIT_ATTRIBUTES } from '../audits/graphQL'
import {
  CONVERSATION_THREAD_ATTRIBUTES,
  CONVERSATION_THREAD_RESOURCE_LINK_ATTRIBUTES,
  KNOCK_MESSAGE_LOG_ATTRIBUTES,
} from '../conversationThreads/graphQL'
import {
  PROJECT_LOCATION_RESOURCE_LINK_WITH_PARENT_ATTRIBUTES,
  PROJECT_LOCATION_WITH_PARENT_ATTRIBUTES,
} from '../projects/graphQL'

export const DEFECT_ATTRIBUTES = gql`
  fragment defectAttributes on Defect {
    id
    createdAt
    updatedAt
    archivedAt
    completedAt
    distributedAt

    dueDate
    status
    priority

    title
    description
    type
    order
    overdue
    tags

    nextDefectId
    previousDefectId

    assigner {
      user {
        firstName
        lastName
      }
    }

    project {
      id
      name
      status
      timezone
    }

    assignees {
      id
      worker {
        id
        user {
          id
          firstName
          lastName
        }
      }
      adminUser {
        id
        user {
          id
          firstName
          lastName
        }
      }
      subcontractor {
        id
        company {
          name
        }
      }
    }

    attachments {
      id
      url
      fileName
      thumbnail {
        small
      }
    }

    location {
      ...projectLocationWithParentAttributes
    }

    conversation {
      ...conversationThreadAttributes
    }
  }
  ${PROJECT_LOCATION_WITH_PARENT_ATTRIBUTES}
  ${CONVERSATION_THREAD_ATTRIBUTES}
`

export const GET_DEFECT = gql`
  query defect_UI($id: String!) {
    defect(id: $id) {
      ...defectAttributes
    }
    knockMessageLogs_admin(filter: { defectId: $id }) {
      ...knockMessageLogAttributes
    }
  }
  ${DEFECT_ATTRIBUTES}
  ${KNOCK_MESSAGE_LOG_ATTRIBUTES}
`

export const GET_DEFECT_WITH_AUDIT_LOGS = gql`
  query defectWithAuditLogs_UI($id: String!) {
    defect(id: $id) {
      ...defectAttributes
    }
    auditLogsByResourceId(id: $id) {
      ...auditAttributes
    }
  }
  ${DEFECT_ATTRIBUTES}
  ${AUDIT_ATTRIBUTES}
`

export const GET_DEFECTS = gql`
  query defects_UI(
    $filter: DefectFilterInput!
    $pagination: PaginationInput
    $sort: DefectSortInput
  ) {
    defects(filter: $filter, pagination: $pagination, sort: $sort) {
      id
      distributedAt
      status
      order
      title
      type
      description
      dueDate
      overdue
      status
      priority
      tags

      project {
        id
        name
        status
      }

      assignees {
        id
        worker {
          id
          user {
            id
            firstName
            lastName
          }
        }
        adminUser {
          id
          user {
            id
            firstName
            lastName
          }
        }
        subcontractor {
          id
          company {
            name
          }
        }
      }

      attachments {
        id
        url
        fileName
        thumbnail {
          small
        }
      }

      location {
        ...projectLocationWithParentAttributes
      }
    }
    defectsAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${PROJECT_LOCATION_WITH_PARENT_ATTRIBUTES}
`

export const CREATE_DEFECT = gql`
  mutation createDefect_UI($input: CreateDefectInput!) {
    createDefect(input: $input) {
      ...defectAttributes
    }
  }
  ${DEFECT_ATTRIBUTES}
`

export const UPDATE_DEFECT = gql`
  mutation updateDefect_UI($id: String!, $input: UpdateDefectInput!) {
    updateDefect(id: $id, input: $input) {
      ...defectAttributes
    }
  }
  ${DEFECT_ATTRIBUTES}
`

export const UPDATE_DEFECT_STATUS = gql`
  mutation updateDefectStatus_UI($id: String!, $status: DefectStatus!) {
    updateDefectStatus(id: $id, status: $status) {
      id
    }
  }
`

export const UPDATE_DEFECTS_STATUS = gql`
  mutation updateDefectsStatus_UI($ids: [String!]!, $status: DefectStatus!) {
    updateDefectsStatus(ids: $ids, status: $status) {
      id
    }
  }
`

export const UPDATE_DEFECTS_ASSIGNEES = gql`
  mutation updateDefectsAssignees_UI(
    $ids: [String!]!
    $assignees: [AssigneeInput!]!
  ) {
    updateDefectsAssignees(ids: $ids, assignees: $assignees) {
      id
    }
  }
`

export const UPDATE_DEFECTS_DUE_DATE = gql`
  mutation updateDefectsDueDate_UI($ids: [String!]!, $dueDate: DateTime!) {
    updateDefectsDueDate(ids: $ids, dueDate: $dueDate) {
      id
    }
  }
`

export const UPDATE_DEFECT_VIA_RESOURCE_LINK = gql`
  mutation updateDefectViaResourceLink_UI(
    $resourceLinkSlug: String!
    $defectId: String!
    $input: UpdateResourceLinkDefectInput!
  ) {
    updateDefectViaResourceLink(
      resourceLinkSlug: $resourceLinkSlug
      defectId: $defectId
      input: $input
    ) {
      id
    }
  }
`

export const CREATE_DEFECT_MESSAGE_VIA_RESOURCE_LINK = gql`
  mutation createDefectMessageViaResourceLink_UI(
    $resourceLinkSlug: String!
    $defectId: String!
    $input: CreateConversationMessageInput!
  ) {
    createDefectMessageViaResourceLink(
      resourceLinkSlug: $resourceLinkSlug
      defectId: $defectId
      input: $input
    ) {
      id
    }
  }
`

export const SEND_DEFECTS = gql`
  mutation sendDefects_UI($ids: [String!]!, $projectId: String!) {
    sendDefects(ids: $ids, projectId: $projectId) {
      id
    }
  }
`

const WORKER_DEFECT_ATTRIBUTES = gql`
  fragment workerDefectAttributes on WorkerDefectEntry {
    id
    createdAt
    updatedAt
    archivedAt
    completedAt

    dueDate
    status
    priority

    title
    description
    type
    order
    overdue

    project {
      id
      name
      status
    }

    attachments {
      id
      url
      fileName
      thumbnail {
        small
      }
    }

    location {
      ...projectLocationResourceLinkWithParentAttributes
    }
  }
  ${PROJECT_LOCATION_RESOURCE_LINK_WITH_PARENT_ATTRIBUTES}
`

export const MY_DEFECTS = gql`
  query myDefects_UI($projectId: String) {
    myDefects(projectId: $projectId) {
      ...workerDefectAttributes
    }
  }
  ${WORKER_DEFECT_ATTRIBUTES}
`

export const MY_DEFECT = gql`
  query myDefect_UI($id: String!) {
    myDefect(id: $id) {
      ...workerDefectAttributes

      conversation {
        ...conversationThreadResourceLinkAttributes
      }
    }
  }
  ${WORKER_DEFECT_ATTRIBUTES}
  ${CONVERSATION_THREAD_RESOURCE_LINK_ATTRIBUTES}
`

export const UPDATE_MY_DEFECT = gql`
  mutation updateMyDefect_UI($id: String!, $input: UpdateMyDefectInput!) {
    updateMyDefect(id: $id, input: $input) {
      ...workerDefectAttributes

      conversation {
        ...conversationThreadResourceLinkAttributes
      }
    }
  }
  ${WORKER_DEFECT_ATTRIBUTES}
  ${CONVERSATION_THREAD_RESOURCE_LINK_ATTRIBUTES}
`

export const CREATE_MY_DEFECT_MESSAGE = gql`
  mutation createMyDefectMessage_UI(
    $id: String!
    $input: CreateConversationMessageInput!
  ) {
    createMyDefectMessage(id: $id, input: $input) {
      id
    }
  }
`

export const GET_ALL_ASSIGNEES = gql`
  query defectAssignees(
    $projectId: String!
    $pagination: PaginationInput
    $searchString: String
    $adminUserFilter: AdminUserFilterInput
    $adminUserSort: AdminUserSortInput
    $filter: SubcontractorFilterInput
    $sort: SubcontractorSortInput
  ) {
    workersByProject(projectId: $projectId) {
      id
      user {
        firstName
        lastName
      }
      trade
    }
    subcontractorsByProject(
      projectId: $projectId
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      id
      company {
        name
      }
    }
    adminUsers(
      filter: $adminUserFilter
      pagination: $pagination
      sort: $adminUserSort
      searchString: $searchString
    ) {
      id
      user {
        firstName
        lastName
      }
    }
  }
`
